import React, { useEffect, useMemo, useState } from 'react';
import PresignedAvatar from '../common/PresignedAvatar';
import ConfirmModal from '../common/ConfirmModal';
import {
  fetchDayActivityTimeline,
  fetchMemberSummary,
  markSharedUserOnLeave,
  revertSharedUserLeave,
  saveMemberSupervisorNotes,
} from '../../services/attendanceService';
import { loadSharedUserDayStatus } from '../../utils/loadSharedUserDayStatus';
import { TaskStatusIcon, formatTaskStatusLabel, TASK_STATUS_STYLES } from './attendanceTaskStatus';

const STATE_LABELS = {
  CHECKED_IN: 'Checked in',
  ON_BREAK: 'On break',
  LOGGED_OUT: 'Logged out',
  NOT_CHECKED_IN: 'Not checked in',
  ON_LEAVE: 'On leave',
  HOLIDAY: 'Holiday',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TYPE_META = {
  CHECK_IN: { title: 'Logged in', dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800', line: 'bg-emerald-400/70' },
  BREAK_START: { title: 'Break started', dot: 'bg-amber-500 ring-amber-200 dark:ring-amber-800', line: 'bg-amber-300/80' },
  BREAK_END: { title: 'Joined back', dot: 'bg-sky-500 ring-sky-200 dark:ring-sky-800', line: 'bg-sky-300/80' },
  RELOGIN: { title: 'Relogged in', dot: 'bg-teal-500 ring-teal-200 dark:ring-teal-800', line: 'bg-teal-300/80' },
  CHECK_OUT: { title: 'Logged out', dot: 'bg-rose-500 ring-rose-200 dark:ring-rose-800', line: 'bg-rose-300/70' },
  CALL_JOIN: { title: 'Joined call', dot: 'bg-violet-500 ring-violet-200 dark:ring-violet-800', line: 'bg-violet-300/80' },
  CALL_LEAVE: { title: 'Left call', dot: 'bg-indigo-500 ring-indigo-200 dark:ring-indigo-800', line: 'bg-indigo-300/80' },
  LEAVE_APPLY: { title: 'Leave applied', dot: 'bg-rose-500 ring-rose-200 dark:ring-rose-800', line: 'bg-rose-300/80' },
  LEAVE_REQUEST: { title: 'Leave requested', dot: 'bg-rose-500 ring-rose-200 dark:ring-rose-800', line: 'bg-rose-300/80' },
  ABSENT_MARKED: { title: 'Marked absent', dot: 'bg-orange-500 ring-orange-200 dark:ring-orange-800', line: 'bg-orange-300/80' },
};

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const parseDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const userInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const HALF_LEAVE_STYLE = {
  background: 'linear-gradient(90deg, #22c55e 0 50%, #ef4444 50% 100%)',
};

const kindClass = (kind, selected) => {
  const ring = selected ? ' ring-2 ring-offset-1 ring-emerald-500' : '';
  if (kind === 'ATTENDED') return `bg-emerald-500 text-white${ring}`;
  if (kind === 'LEAVE') return `bg-red-500 text-white${ring}`;
  if (kind === 'ABSENT') return `bg-orange-500 text-white${ring}`;
  if (kind === 'HALF_LEAVE') return `text-white${ring}`;
  if (kind === 'HOLIDAY') return `bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200${ring}`;
  return `bg-white dark:bg-gray-900 text-gray-900 dark:text-white${ring}`;
};

const buildMonths = (startDate, endDate, daysByDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const cells = [];
    for (let i = 0; i < firstDay.getDay(); i += 1) cells.push(null);
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      const date = formatDateInput(new Date(year, month, day));
      const inRange = date >= startDate && date <= endDate;
      cells.push({
        date,
        day,
        inRange,
        info: daysByDate.get(date) || null,
      });
    }
    const holidayCount = cells.filter((cell) => cell?.inRange && cell.info?.kind === 'HOLIDAY').length;
    months.push({
      key: `${year}-${month}`,
      title: firstDay.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      holidayCount,
      cells,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

const DayTimelineInline = ({ timeline }) => {
  const entries = timeline?.entries || [];
  if (!entries.length) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 py-3">
        No attendance activity for this day.
      </p>
    );
  }
  return (
    <ol className="relative pl-1">
      {entries.map((entry, index) => {
        const meta = TYPE_META[entry.type] || {
          title: entry.title || entry.type,
          dot: 'bg-gray-400 ring-gray-200 dark:ring-gray-700',
          line: 'bg-gray-300/80 dark:bg-gray-600/80',
        };
        const isLast = index === entries.length - 1;
        return (
          <li key={entry.eventId || `${entry.type}-${index}`} className="relative flex gap-3 pb-4 last:pb-0">
            <div className="relative flex flex-col items-center w-4 flex-shrink-0">
              <span className={`relative z-[1] mt-1 w-3 h-3 rounded-full ring-4 ${meta.dot}`} aria-hidden />
              {!isLast && (
                <span className={`absolute top-3.5 bottom-[-1rem] w-0.5 rounded-full ${meta.line}`} aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1 -mt-0.5">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                  {entry.title || meta.title}
                </p>
                <p className="text-[11px] font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                  {entry.timeLabel || '—'}
                </p>
              </div>
              {entry.subtitle ? (
                <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">{entry.subtitle}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const CompletedTasksInline = ({ tasks = [] }) => {
  const completed = (tasks || []).map((task) => ({ ...task, status: task.status || 'COMPLETED' }));
  return (
    <div className="space-y-1.5">
      {completed.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-2">No tasks completed this day.</p>
      ) : (
        completed.map((task) => (
          <div
            key={task.id || task.title}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-2"
          >
            <div className="flex items-start gap-2">
              <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full ${TASK_STATUS_STYLES.COMPLETED}`}>
                <TaskStatusIcon status="COMPLETED" className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-snug">{task.title}</p>
                <div className="flex items-center justify-between gap-1 mt-1">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${TASK_STATUS_STYLES.COMPLETED}`}>
                    {formatTaskStatusLabel('COMPLETED')}
                  </span>
                  {task.minutes != null && task.minutes > 0 ? (
                    <span className="text-[10px] font-bold tabular-nums text-blue-600">{task.minutes}m</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const MemberSummaryModal = ({ member, onClose }) => {
  const today = useMemo(() => formatDateInput(new Date()), []);
  const [preset, setPreset] = useState('7d');
  const [startDate, setStartDate] = useState(() => formatDateInput(addDays(new Date(), -6)));
  const [endDate, setEndDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedHint, setSavedHint] = useState('');
  const [dayTimeline, setDayTimeline] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [dayStatus, setDayStatus] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);
  const [dayRefresh, setDayRefresh] = useState(0);
  const [marking, setMarking] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const applyPreset = (nextPreset) => {
    setPreset(nextPreset);
    const end = new Date();
    if (nextPreset === '7d') {
      setStartDate(formatDateInput(addDays(end, -6)));
      setEndDate(formatDateInput(end));
    } else if (nextPreset === '1m') {
      setStartDate(formatDateInput(addDays(end, -29)));
      setEndDate(formatDateInput(end));
    }
  };

  useEffect(() => {
    if (!member?.userId || !startDate || !endDate) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchMemberSummary(member.userId, startDate, endDate);
        if (cancelled) return;
        setSummary(data);
        const nextSelected = (data?.days || []).some((day) => day.date === selectedDate)
          ? selectedDate
          : (data?.periodEnd || endDate);
        setSelectedDate(nextSelected);
        const selected = (data?.days || []).find((day) => day.date === nextSelected);
        setNote(selected?.note || '');
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load summary');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [member?.userId, startDate, endDate]);

  useEffect(() => {
    if (!member?.userId || !selectedDate) return undefined;
    let cancelled = false;
    const loadDay = async () => {
      setLoadingDay(true);
      setDayTimeline(null);
      setCompletedTasks([]);
      setDayStatus(null);
      try {
        const [timeline, status] = await Promise.all([
          fetchDayActivityTimeline(selectedDate, member.userId).catch(() => null),
          loadSharedUserDayStatus({ userId: member.userId, date: selectedDate }).catch((err) => {
            if (err?.code === 'NO_SESSION') return { type: 'NONE' };
            throw err;
          }),
        ]);
        if (cancelled) return;
        setDayTimeline(timeline);
        if (status?.type === 'DAY') {
          setCompletedTasks(
            (status.completedTasks || []).filter((task) => {
              const statusCode = String(task.status || 'COMPLETED').toUpperCase();
              return statusCode === 'COMPLETED' || statusCode === 'DONE';
            }),
          );
          setDayStatus(status);
        } else if (status?.type === 'LEAVE') {
          setCompletedTasks([]);
          setDayStatus(status);
        } else {
          setCompletedTasks([]);
          setDayStatus(status || { type: 'NONE' });
        }
      } catch (err) {
        if (!cancelled) {
          setDayStatus({ type: 'NONE' });
        }
      } finally {
        if (!cancelled) setLoadingDay(false);
      }
    };
    loadDay();
    return () => { cancelled = true; };
  }, [member?.userId, selectedDate, dayRefresh]);

  const daysByDate = useMemo(() => {
    const map = new Map();
    (summary?.days || []).forEach((day) => map.set(day.date, day));
    return map;
  }, [summary]);

  const months = useMemo(
    () => buildMonths(summary?.periodStart || startDate, summary?.periodEnd || endDate, daysByDate),
    [summary, startDate, endDate, daysByDate],
  );

  const datedNotes = useMemo(
    () => (summary?.days || []).filter((day) => String(day.note || '').trim()).reverse(),
    [summary],
  );

  const selectDate = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setSavedHint('');
    setNote(daysByDate.get(date)?.note || '');
  };

  const reloadSummary = async () => {
    if (!member?.userId || !startDate || !endDate) return;
    const data = await fetchMemberSummary(member.userId, startDate, endDate);
    setSummary(data);
    const selected = (data?.days || []).find((day) => day.date === selectedDate);
    setNote(selected?.note || '');
    setDayRefresh((n) => n + 1);
  };

  const handleConfirmMark = async () => {
    if (!member?.userId || !selectedDate || !confirmAction) return;
    setMarking(true);
    setError('');
    try {
      if (confirmAction === 'revert') {
        await revertSharedUserLeave(member.userId, selectedDate);
      } else {
        await markSharedUserOnLeave({
          userId: member.userId,
          date: selectedDate,
          markKind: confirmAction === 'absent' ? 'ABSENT' : confirmAction === 'holiday' ? 'HOLIDAY' : 'LEAVE',
          leaveType: confirmAction === 'absent' ? 'ABSENT' : confirmAction === 'holiday' ? 'HOLIDAY' : 'FULL_DAY',
        });
      }
      setConfirmAction(null);
      await reloadSummary();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update attendance');
      setConfirmAction(null);
    } finally {
      setMarking(false);
    }
  };

  const handleSaveNote = async () => {
    if (!member?.userId || !selectedDate) return;
    setSaving(true);
    setError('');
    setSavedHint('');
    try {
      const data = await saveMemberSupervisorNotes(member.userId, note, selectedDate, startDate, endDate);
      setSummary(data);
      const selected = (data?.days || []).find((day) => day.date === selectedDate);
      setNote(selected?.note || '');
      setSavedHint('Note saved for this date. Only you can see it.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const name = summary?.userName || member?.userName || 'User';
  const state = summary?.attendanceState || member?.attendanceState || 'NOT_CHECKED_IN';
  const selectedInfo = daysByDate.get(selectedDate);
  const canMarkSelectedDay = Boolean(selectedDate && selectedDate <= today);
  const hasMarkedDay = selectedInfo?.kind === 'LEAVE'
    || selectedInfo?.kind === 'ABSENT'
    || selectedInfo?.kind === 'HALF_LEAVE'
    || selectedInfo?.leaveType === 'HOLIDAY';
  const confirmCopy = confirmAction === 'absent'
    ? { title: 'Mark absent', message: `Mark ${name} absent for ${selectedInfo?.dateLabel || selectedDate}?`, confirmText: 'Mark absent', type: 'warning' }
    : confirmAction === 'leave'
      ? { title: 'Mark on leave', message: `Mark ${name} on leave for ${selectedInfo?.dateLabel || selectedDate}?`, confirmText: 'Mark leave', type: 'danger' }
      : confirmAction === 'holiday'
        ? { title: 'Mark holiday', message: `Mark ${name} on holiday for ${selectedInfo?.dateLabel || selectedDate}?`, confirmText: 'Mark holiday', type: 'info' }
        : { title: 'Revert mark', message: `Revert the leave, absence, or holiday mark for ${name} on ${selectedInfo?.dateLabel || selectedDate}?`, confirmText: 'Revert', type: 'info' };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-6 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-6xl h-[92vh] rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <PresignedAvatar
              fileKey={summary?.profilePicture || member?.profilePicture}
              name={name}
              initials={userInitials(name)}
              className="w-12 h-12 rounded-full object-cover"
              fallbackClassName="w-12 h-12 rounded-full flex items-center justify-center bg-emerald-600 text-white text-sm font-semibold"
            />
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">{name}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Supervisor:{' '}
                {summary?.supervisorName
                  ? `${summary.supervisorName}${summary.supervisorUsername ? ` @${summary.supervisorUsername}` : ''}`
                  : 'Not assigned'}
                {' · '}
                {STATE_LABELS[state] || state}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => applyPreset('7d')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${preset === '7d' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
          >
            Last 7 days
          </button>
          <button
            type="button"
            onClick={() => applyPreset('1m')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${preset === '1m' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
          >
            Last 1 month
          </button>
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <span>Date range</span>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => {
                setPreset('custom');
                setStartDate(e.target.value);
              }}
              className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            />
            <span>to</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={today}
              onChange={(e) => {
                setPreset('custom');
                setEndDate(e.target.value);
              }}
              className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            />
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Attended</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500" /> Leave</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-orange-500" /> Absent</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded" style={HALF_LEAVE_STYLE} /> Half day
            </span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-400" /> Holiday</span>
          </div>
        </div>

        {error ? (
          <div className="mx-5 mt-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.95fr)]">
          <div className="min-h-0 overflow-y-auto p-5 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Presence</p>
                <p className="text-lg font-bold tabular-nums">{summary?.presenceDays ?? 0}</p>
              </div>
              <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50/80 dark:bg-rose-950/30 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Absence</p>
                <p className="text-lg font-bold tabular-nums">{summary?.absenceDays ?? 0}</p>
              </div>
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/30 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Avg productivity</p>
                <p className="text-lg font-bold tabular-nums">
                  {summary?.averageProductivityPercent != null ? `${summary.averageProductivityPercent}%` : '—'}
                </p>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500">Loading calendar…</p>
            ) : (
              months.map((month) => (
                <div key={month.key}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{month.title}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
                      {month.holidayCount} Holidays
                    </span>
                  </div>
                  <div className="grid grid-cols-7 text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    {WEEKDAYS.map((label) => (
                      <div key={label} className="px-1 py-1 text-center">{label}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    {month.cells.map((cell, index) => {
                      if (!cell) {
                        return <div key={`empty-${index}`} className="min-h-[72px] bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800" />;
                      }
                      const kind = cell.inRange ? (cell.info?.kind || 'NONE') : 'OUT';
                      const selected = cell.date === selectedDate;
                      return (
                        <button
                          key={cell.date}
                          type="button"
                          disabled={!cell.inRange}
                          onClick={() => selectDate(cell.date)}
                          className={`min-h-[72px] p-1.5 text-left border border-gray-100 dark:border-gray-800 ${
                            cell.inRange ? kindClass(kind, selected) : 'bg-gray-50 dark:bg-gray-800/40 text-gray-400'
                          }`}
                          style={cell.inRange && kind === 'HALF_LEAVE' ? HALF_LEAVE_STYLE : undefined}
                        >
                          <div className="text-sm font-semibold">{cell.day}</div>
                          {cell.inRange && kind === 'HOLIDAY' ? (
                            <div className="text-[10px] truncate text-gray-600 dark:text-gray-300">
                              {cell.info?.holidayName || 'Holiday'}
                            </div>
                          ) : null}
                          {cell.inRange && (kind === 'LEAVE' || kind === 'HALF_LEAVE' || kind === 'ABSENT') ? (
                            <div className="text-[10px] font-semibold">
                              {cell.info?.leaveLabel
                                || (kind === 'ABSENT' ? 'Absent' : kind === 'HALF_LEAVE' ? 'Half day' : 'Leave')}
                            </div>
                          ) : null}
                          {cell.inRange && cell.info?.productivityPercent != null ? (
                            <div className="text-[11px] font-bold tabular-nums mt-1">
                              {cell.info.productivityPercent}%
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="min-h-0 overflow-y-auto border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 p-5 space-y-4 bg-gray-50/80 dark:bg-gray-950/40">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {selectedInfo?.dateLabel || selectedDate}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {dayTimeline?.checkInTime ? `In ${dayTimeline.checkInTime}` : 'Not logged in'}
                {dayTimeline?.checkOutTime ? ` · Out ${dayTimeline.checkOutTime}` : dayTimeline?.activeDay ? ' · In progress' : ''}
              </p>
            </div>

            {loadingDay ? (
              <p className="text-sm text-gray-500">Loading this day’s activity…</p>
            ) : (
              <>
                {dayStatus?.type === 'LEAVE' || selectedInfo?.kind === 'LEAVE' || selectedInfo?.kind === 'HALF_LEAVE' ? (
                  <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-800 dark:text-red-200">
                    {selectedInfo?.leaveLabel
                      || dayStatus?.leaveLabel
                      || (selectedInfo?.kind === 'HALF_LEAVE' ? 'Half day leave' : 'On leave')}
                  </div>
                ) : null}
                {selectedInfo?.kind === 'ABSENT' ? (
                  <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30 px-3 py-2 text-sm text-orange-800 dark:text-orange-200">
                    {selectedInfo?.leaveLabel || 'Absent'}
                  </div>
                ) : null}
                {canMarkSelectedDay ? (
                  <div className="flex flex-wrap gap-2">
                    {hasMarkedDay ? (
                      <button
                        type="button"
                        disabled={marking}
                        onClick={() => setConfirmAction('revert')}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                      >
                        Revert mark
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={marking}
                          onClick={() => setConfirmAction('leave')}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Mark on leave
                        </button>
                        <button
                          type="button"
                          disabled={marking}
                          onClick={() => setConfirmAction('absent')}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                        >
                          Mark absent
                        </button>
                        <button
                          type="button"
                          disabled={marking}
                          onClick={() => setConfirmAction('holiday')}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50"
                        >
                          Mark holiday
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
                {selectedInfo?.kind === 'HOLIDAY' ? (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                    {selectedInfo.holidayName || 'Holiday'}
                  </div>
                ) : null}

                <section>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Timeline
                  </p>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-3">
                    <DayTimelineInline timeline={dayTimeline} />
                  </div>
                </section>

                <section>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Completed tasks
                    {completedTasks.length ? ` · ${completedTasks.length}` : ''}
                  </p>
                  <CompletedTasksInline tasks={completedTasks} />
                </section>
              </>
            )}

            <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Private note
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                Only you can see this. {name} cannot.
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Add a note for this date…"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {savedHint ? (
                <p className="mt-1.5 text-xs text-emerald-600 dark:text-emerald-400">{savedHint}</p>
              ) : null}
              <button
                type="button"
                disabled={saving || loading || !selectedDate}
                onClick={handleSaveNote}
                className="mt-2 w-full px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save note for this date'}
              </button>
            </div>

            {datedNotes.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Notes in this range
                </p>
                {datedNotes.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => selectDate(day.date)}
                    className={`w-full text-left rounded-lg border px-3 py-2 ${
                      day.date === selectedDate
                        ? 'border-emerald-400 bg-white dark:bg-gray-900'
                        : 'border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/60'
                    }`}
                  >
                    <p className="text-xs font-semibold text-gray-900 dark:text-white">{day.dateLabel}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">{day.note}</p>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={Boolean(confirmAction)}
        onClose={() => {
          if (!marking) setConfirmAction(null);
        }}
        onConfirm={handleConfirmMark}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmText={marking ? 'Updating…' : confirmCopy.confirmText}
        type={confirmCopy.type}
      />
    </div>
  );
};

export default MemberSummaryModal;
